import { Component } from "react";

/**
 * The app had no error boundary at all, so any throw during render took every
 * route to a blank page — including the one failure that is guaranteed to
 * happen: a code-split chunk going missing after a deploy.
 *
 * lazyWithRetry handles the first chunk failure by reloading. This catches
 * what is left: a second failure (so the reload did not help and looping would
 * be worse), and any ordinary render error. Both get a page that says what
 * happened and offers the two things that actually work — reload, or leave.
 *
 * Deliberately not a full crash reporter. It records nothing and blames
 * nothing; it exists so a broken route is a broken route rather than a white
 * screen with no way out.
 */
export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept to the console rather than sent anywhere: there is no error
    // reporting configured yet, and inventing a destination for user data is
    // not this component's call.
    console.error("[route] render failed", error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = /Loading chunk|Loading CSS chunk|dynamically imported module/i
      .test(error?.message || "");

    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <div className="max-w-md w-full" data-testid="route-error">
          <div className="u-label text-stone-600">Something broke</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-stone-950">
            {stale ? "This page moved out from under you." : "This page didn’t load."}
          </h1>
          <p className="mt-3 text-sm text-stone-700">
            {stale
              ? "A new version of NextOpp was released while your tab was open, so the "
                + "file this page needed is no longer there. Reloading picks up the new one."
              : "Something in this page failed while it was rendering. Reloading usually "
                + "clears it. Nothing you had saved is affected."}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => window.location.reload()}
              className="h-10 px-4 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium"
              data-testid="route-error-reload"
            >
              Reload the page
            </button>
            <a
              href="/dashboard"
              className="h-10 px-4 rounded-sm border border-sand bg-white text-sm font-medium
                         inline-flex items-center hover:bg-paper"
            >
              Go to dashboard
            </a>
          </div>
          {/* The message, verbatim, for anyone who reports this. Not a stack —
              that is in the console and means nothing to a job seeker. */}
          <p className="mt-6 font-mono text-[11px] text-stone-500 break-words">
            {String(error?.message || error)}
          </p>
        </div>
      </div>
    );
  }
}
